/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-09 12:30:39
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React from 'react';
import { TextStyle } from '@shopify/polaris';

import { store } from 'src/stores/store';

export const enableThemeSetupV3 = tenantApp => {
  const enableThemeSetupV3 = _.get(tenantApp, 'settings.themeSetupV3.settings.enable', false);
  return enableThemeSetupV3;
};

export const enableThemeSetupV3OnLiveTheme = tenantApp => {
  const enableThemeSetupV3 = _.get(
    tenantApp,
    'settings.themeSetupV3.settings.enableSetupOnLiveTheme',
    false
  );
  return enableThemeSetupV3;
};

export const isThemeV3 = (theme = {}) => {
  const boostCoreV3Enabled = _.get(theme, 'enabledBoostCoreV3', false);
  const recommendation = _.get(theme, 'taeFeatures.recommendation', '') === 'installed';
  const instantSearch = _.get(theme, 'taeFeatures.instantSearch', '') === 'installed';
  const filterCollection = _.get(theme, 'taeFeatures.filterCollection', '') === 'installed';
  const filterSearch = _.get(theme, 'taeFeatures.filterSearch', '') === 'installed';
  const themeLibV3 =
    boostCoreV3Enabled || recommendation || instantSearch || filterCollection || filterSearch;
  return themeLibV3;
};

export const isThemeV3OnlyRecommend = (theme = {}) => {
  const recommendation = _.get(theme, 'taeFeatures.recommendation', '') === 'installed';
  const instantSearch = _.get(theme, 'taeFeatures.instantSearch', '') === 'installed';
  const filterCollection = _.get(theme, 'taeFeatures.filterCollection', '') === 'installed';
  const filterSearch = _.get(theme, 'taeFeatures.filterSearch', '') === 'installed';
  const themeLibV3OnlyRecommend =
    recommendation && !instantSearch && !filterCollection && !filterSearch;
  return themeLibV3OnlyRecommend;
};

export const isThemeTurbo = (theme = {}) => {
  const enabledBoostCore = _.get(theme, 'ssrFeatures.enabledBoostCore');
  const enabledISW = _.get(theme, 'ssrFeatures.enabledISW');
  const enabledAppBlockCollectionPage = _.get(theme, 'ssrFeatures.enabledAppBlock.collectionPage');
  const enabledAppBlockSearchPage = _.get(theme, 'ssrFeatures.enabledAppBlock.searchPage');
  const enabledAppBlockRecommendation = _.get(theme, 'ssrFeatures.enabledAppBlock.recommendation');
  const recommendationWidgets = _.get(theme, 'ssrFeatures.recommendationWidgets');

  return (
    enabledBoostCore ||
    enabledISW ||
    enabledAppBlockCollectionPage ||
    enabledAppBlockSearchPage ||
    enabledAppBlockRecommendation ||
    recommendationWidgets
  );
};

/**
 * Check if theme installed v1,v2,v3
 * @param {*} theme
 * @returns
 */
export const hasOldThemeVersion = (theme = {}) => {
  return (
    isThemeV3(theme) || theme?.feLibVersion?.includes('v2') || theme?.feLibVersion?.includes('v1')
  );
};

// Check if have themeV3 in list theme
export const hasThemeV3 = (themes = {}) => {
  const themeV3 = themes.find(theme => isThemeV3(theme));
  if (themeV3) return true;
  return false;
};

// Message for each live theme status
export const getLiveThemeStatusHtml = (status, i18n) => {
  switch (status) {
    case 'wellInstall':
      return (
        <p className="theme-status-text">
          {i18n.translate('AdminHTML.Dashboard.ThemeSetupStatus.wellInstall_Content', {
            t: (
              <TextStyle variation="positive">
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.Dashboard.ThemeSetupStatus.wellInstall_Content_t')}
                </TextStyle>
              </TextStyle>
            )
          })}
        </p>
      );
    case 'noTheme':
      return (
        <p className="theme-status-text">
          {i18n.translate('AdminHTML.Dashboard.ThemeSetupStatus.noTheme_Content', {
            t: (
              <TextStyle variation="negative">
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.Dashboard.ThemeSetupStatus.noTheme_Content_t')}
                </TextStyle>
              </TextStyle>
            )
          })}
        </p>
      );
    case 'notLive':
      return (
        <p className="theme-status-text">
          {i18n.translate('AdminHTML.Dashboard.ThemeSetupStatus.notLive_Content', {
            t: (
              <TextStyle variation="strong">
                <span style={{ color: 'var(--p-decorative-one-surface)' }}>
                  {i18n.translate('AdminHTML.Dashboard.ThemeSetupStatus.notLive_Content_t')}
                </span>
              </TextStyle>
            )
          })}
        </p>
      );
    default:
      return null;
  }
};

/* Get live theme status */
export const getLiveThemeStatus = (loading: boolean, mainTheme: { published: string }) => {
  if (loading) return 'loading';
  if (_.isEmpty(mainTheme)) return 'noTheme';

  const { published } = mainTheme;

  return published ? 'wellInstall' : 'notLive';
};

/**
 * Check if selecting theme OS 2.0 for setup theme
 *
 * @returns {boolean} true | false
 */
export function checkSelectThemeOS20() {
  const currentState = store.getState();
  const isOS20 = _.get(currentState.themeSetup, 'isOS20', false);

  return isOS20;
}

// Message for each live theme status v3
export const getLiveThemeStatusV3Html = (status, i18n) => {
  switch (status) {
    case 'liveIsV1':
      return (
        <p className="theme-status-text">
          {i18n.translate('AdminHTML.Dashboard.ThemeSetupStatus.v3_wellInstalledV1_Content', {
            t: (
              <TextStyle variation="positive">
                <TextStyle variation="strong">
                  {i18n.translate(
                    'AdminHTML.Dashboard.ThemeSetupStatus.v3_wellInstalledV1_Content_t'
                  )}
                </TextStyle>
              </TextStyle>
            )
          })}
        </p>
      );
    case 'liveIsV2':
      return (
        <p className="theme-status-text">
          {i18n.translate('AdminHTML.Dashboard.ThemeSetupStatus.v3_wellInstalledV2_Content', {
            t: (
              <TextStyle variation="positive">
                <TextStyle variation="strong">
                  {i18n.translate(
                    'AdminHTML.Dashboard.ThemeSetupStatus.v3_wellInstalledV2_Content_t'
                  )}
                </TextStyle>
              </TextStyle>
            )
          })}
        </p>
      );
    case 'liveIsV3':
      return (
        <p className="theme-status-text">
          {i18n.translate('AdminHTML.Dashboard.ThemeSetupStatus.v3_wellInstalledV3_Content', {
            t: (
              <TextStyle variation="positive">
                <TextStyle variation="strong">
                  {i18n.translate(
                    'AdminHTML.Dashboard.ThemeSetupStatus.v3_wellInstalledV3_Content_t'
                  )}
                </TextStyle>
              </TextStyle>
            )
          })}
        </p>
      );
    case 'noTheme':
      return (
        <p className="theme-status-text">
          {i18n.translate('AdminHTML.Dashboard.ThemeSetupStatus.v3_noTheme_Content', {
            t: (
              <TextStyle variation="negative">
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.Dashboard.ThemeSetupStatus.v3_noTheme_Content_t')}
                </TextStyle>
              </TextStyle>
            )
          })}
        </p>
      );
    case 'notLive':
      return (
        <p className="theme-status-text">
          {i18n.translate('AdminHTML.Dashboard.ThemeSetupStatus.v3_noLive_Content', {
            t1: (
              <TextStyle variation="strong">
                <span style={{ color: 'var(--p-decorative-one-surface)' }}>
                  {i18n.translate('AdminHTML.Dashboard.ThemeSetupStatus.v3_noLive_Content_t1')}
                </span>
              </TextStyle>
            ),
            t2: (
              <TextStyle variation="positive">
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.Dashboard.ThemeSetupStatus.v3_noLive_Content_t2')}
                </TextStyle>
              </TextStyle>
            )
          })}
        </p>
      );
    default:
      return null;
  }
};

/* Get live theme status v3 */
export const getLiveThemeStatusV3 = (loading: boolean, mainTheme: { published: string }) => {
  if (loading) return 'loading';
  if (_.isEmpty(mainTheme)) return 'noTheme';

  const { published } = mainTheme;
  const liveThemeIsThemeV3 = isThemeV3(mainTheme);

  if (!published) return 'notLive';
  if (liveThemeIsThemeV3) return 'liveIsV3';
  if (mainTheme.themeLibV2) return 'liveIsV2';
  return 'liveIsV1';
};

export const enableShopifyIntegration = tenantApp => {
  const result = _.get(tenantApp, 'settings.shopifyIntegration.settings.enable', false);
  return result;
};

export const getVersionText = (theme, i18n) => {
  const textTemplateVersion = 'AdminHTML.ThemeSetup.NewThemeSetup.list_theme_info_version';
  const textTemplateVersionTurbo =
    'AdminHTML.ThemeSetup.NewThemeSetup.list_theme_info_version_turbo';
  const { themeLibV2 } = theme;
  const themeLibV3 = isThemeV3(theme) && !isThemeV3OnlyRecommend(theme);
  const themeTurbo = isThemeTurbo(theme);

  if (themeTurbo) return i18n.translate(textTemplateVersionTurbo);
  if (themeLibV3)
    return i18n.translate(textTemplateVersion, {
      v: '3.0'
    });
  if (themeLibV2)
    return i18n.translate(textTemplateVersion, {
      v: '2.0'
    });
  return i18n.translate(textTemplateVersion, {
    v: '1.0'
  });
};

export const getVersionTextSI = (theme, i18n) => {
  const { themeLibV2, feLibVersion } = theme;
  const themeLibV3 = isThemeV3(theme) && !isThemeV3OnlyRecommend(theme);
  const themeTurbo = isThemeTurbo(theme);

  const textTemplateVersionTurbo =
    'AdminHTML.ShopifyIntegration.ScreenList.turbo';
  if (themeTurbo) return i18n.translate(textTemplateVersionTurbo);
  if (themeLibV3) {
    return 'Ver. 3.0';
  }

  if (themeLibV2 || feLibVersion?.includes('v2')) {
    return 'Ver. 2.0'
  }

  if (feLibVersion?.includes('v1')) {
    return 'Ver. 1.0'
  }

  return i18n.translate('AdminHTML.ShopifyIntegration.ScreenList.notIntegrated')
};